import { memo, useRef, useState } from 'react';
import { Divider, IconButton, ListItemText, ListItem, Grid, Box, Typography, Stack } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { roundDoubleNumbers } from '../../helpers/currency';
import { FilteredItemProps } from '../../interfaces/interfaces';
import { ServiceItem } from 'orderme-api-integration-client';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { apiKey, appStyleMode, imagesUri } from '../../helpers/clientConfigs';
import { AppStyle } from '../../helpers/appStyle';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import { ProductPrices } from '../productCard/productPrices';
import { RootState } from '../../redux/store';
import { getServicePrice } from '../../helpers/costCounter';
import FriendsBadge from '../productCard/friendsBadge';
import DetergentsIcon from './detergentsIcon';
import SewingIcon from '../../logo/sewing.png';
import { isSewingService } from '../../helpers/servicesHelper';
import { ImageLoader } from '../imgLoader/imgLoader';
import ClothesIcon from '../../logo/laundry-clothes.png';
import { addToCartAsync } from '../../redux/reducers/shoppingCartReducer';

export const FilteredItem = memo(function FilteredItem(props: FilteredItemProps) {
  const dispatch = useAppDispatch();
  const { service } = props;
  const titleRef = useRef<HTMLLIElement | null>(null);
  const { t } = useTranslation();
  const [isPricesVisible, setIsPricesVisible] = useState(false);
  const shoppingCartItems = useAppSelector((state: RootState) => state.shoppingCartState.items);
  const updatingCart = useAppSelector((state: RootState) => state.shoppingCartState.updatingCart);
  const serviceCount: number = shoppingCartItems.find((s) => s.serviceId === service.id)?.count ?? 0;
  const servicePrice: number = getServicePrice(service, serviceCount);
  const isLoggedIn = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const hasAmountPrice = service.prices?.length > 1;
  const isPriceForLoggedInUser = service.originalPrice > servicePrice;
  const showFriendsPrice = !isLoggedIn && isPriceForLoggedInUser;
  const isSewing = isSewingService(service);

  const addProductToCart = (service: ServiceItem) => {
    dispatch(addToCartAsync(service.id));
    if (appStyleMode === AppStyle.Mobile) {
      titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const togglePricesVisible = () => {
    setIsPricesVisible(!isPricesVisible);
  };

  return (
    <Box>
      <ListItem ref={titleRef} sx={{ py: '0px' }}>
        <ListItemText
          primary={
            <Grid
              container
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0, sm: 2 }}
              sx={{
                textAlign: { xs: 'center', sm: 'left' },
              }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid size="auto">
                {service.logoId ? (
                  <ImageLoader
                    src={`${imagesUri}/${service.logoId}?orderme-api-key=${apiKey}&height=500`}
                    sx={{
                      height: '50px',
                      maxWidth: '50px',
                      objectFit: 'cover',
                    }}
                    alt="SalesPoint"
                  />
                ) : service.isItem ? (
                  <DetergentsIcon
                    sx={{
                      color: 'secondary.main',
                      height: '40px',
                      width: '40px',
                      p: '2px',
                    }}
                  />
                ) : (
                  <ImageLoader
                    src={isSewing ? SewingIcon : ClothesIcon}
                    sx={{ maxHeight: '50px', maxWidth: '50px', py: '10px' }}
                    alt={isSewing ? 'sewing' : 'clothes'}
                  />
                )}
              </Grid>
              <Grid size="grow">{service.name}</Grid>
              <Grid size="auto">
                {hasAmountPrice && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{
                      backgroundColor: 'white',
                      pr: '15px',
                    }}
                  >
                    <Typography sx={{ fontSize: '14px' }}>{t('services:bulkDiscount')}</Typography>
                    <InfoIcon sx={{ color: 'infoIcon' }} onClick={togglePricesVisible} />
                  </Stack>
                )}
                <Stack direction="row" alignItems="center" justifyContent="flex-end">
                  {!showFriendsPrice && (
                    <>
                      {service.originalPrice > servicePrice && (
                        <Box color="text.disabled" sx={{ textDecoration: 'line-through', mr: '10px' }}>
                          {roundDoubleNumbers(service.originalPrice)} €
                        </Box>
                      )}
                      <Box>{roundDoubleNumbers(servicePrice)} €</Box>
                    </>
                  )}
                  {showFriendsPrice && (
                    <Stack direction="column" alignItems="flex-end">
                      <Box>{roundDoubleNumbers(service.originalPrice)} €</Box>
                      <Stack direction="row" alignItems="center">
                        <FriendsBadge />
                        <Box sx={{ ml: '5px', fontWeight: 'bold', width: '80px', textAlign: 'right' }}>
                          {roundDoubleNumbers(servicePrice)} €
                        </Box>
                      </Stack>
                    </Stack>
                  )}
                  <IconButton
                    disabled={updatingCart}
                    sx={{
                      pt: '4px',
                      pb: '0px',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                    onClick={() => addProductToCart(service)}
                  >
                    <AddCircleOutlineIcon
                      sx={{ color: updatingCart ? 'grey' : 'secondary.main', height: '35px', width: '35px' }}
                    />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>
          }
        />
      </ListItem>
      <Divider />
      {hasAmountPrice && (
        <ProductPrices
          serviceName={service.name}
          prices={service.prices}
          open={isPricesVisible}
          onClose={togglePricesVisible}
        />
      )}
    </Box>
  );
});
